import {AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {LoadingComponent} from '../../../../../../components/generic/loading/loading.component';
import {ViewService} from '../../../../../shared/service/view.service';
import {PlayerProfileService} from '../../../../service/player-profile.service';
import {RestErrorHelper} from '../../../../../../helper/rest-error-helper';
import {Pagination} from '../../../../../../model/pagination';
import {
  GenericListComponent,
  PageChangeInfo
} from '../../../../../../components/generic/generic-list/generic-list.component';
import {ErrorType} from '../../../../../../model/error-type';
import {MessageComponent} from '../../../../../../components/generic/message/message.component';
import {ColumnType} from '../../../../../../components/generic/generic-list/columnType';
import {StatisticFilter} from '../../../../model/api/StatisticFilter';
import {ConfirmationService, PrimeTemplate} from 'primeng/api';
import {Subscription} from 'rxjs';
import {LayoutHelper} from '../../../../../../helper/layout.helper';
import {PlayerSessionService} from '../../../../service/player-session.service';
import {
  DateTimePresenterComponent
} from '../../../../../shared/common/date-time-presenter/date-time-presenter.component';
import {DomainValuePipe} from '../../../../../../pipes/domain-value.pipe';
import {NgTemplateOutlet} from '@angular/common';
import {DialogService} from 'primeng/dynamicdialog';
import {StatisticMatch} from '../../../../model/statistic-match';
import {
  EventMatchDetailsComponent
} from '../../../../../frontend/player-frontend/event-frontend/event-match-details/event-match-details.component';
import {
  PlayerProfileStatisticsFilterComponent
} from '../player-profile-statistics-filter/player-profile-statistics-filter.component';
import {PagingFilter} from '../../../../../shared/model/paging-filter';
import {FrontendService} from '../../../../../../services/frontend.service';
import {
  PlayerProfileProFeatureComponent
} from '../../../player-profile-register/player-profile-pro-feature/player-profile-pro-feature.component';
import {FormsModule} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-player-profile-statistics-matches',
  standalone: true,
  imports: [
    LoadingComponent,
    MessageComponent,
    GenericListComponent,
    PrimeTemplate,
    DateTimePresenterComponent,
    DomainValuePipe,
    NgTemplateOutlet,
    PlayerProfileStatisticsFilterComponent,
    PlayerProfileProFeatureComponent,
    FormsModule
  ],
  templateUrl: './player-profile-statistics-matches.component.html',
  styleUrl: './player-profile-statistics-matches.component.scss'
})
export class PlayerProfileStatisticsMatchesComponent implements OnInit, AfterViewInit, OnDestroy
{
  public view = inject(ViewService);
  public playerProfileService = inject(PlayerProfileService);
  private playerSessionService = inject(PlayerSessionService);
  private restErrorHelper = inject(RestErrorHelper);
  private dialogService = inject(DialogService);
  private frontendService = inject(FrontendService);
  private translate = inject(TranslateService);
  private confirmationService = inject(ConfirmationService);

  @ViewChild('genericList', {read: GenericListComponent}) genericList: GenericListComponent;
  @ViewChild('dateTemplate') dateTemplate: any;
  @ViewChild('eventInfoTemplate') eventInfoTemplate: any;
  @ViewChild('matchInfoTemplate') matchInfoTemplate: any;
  @ViewChild('participantsTemplate') participantsTemplate: any;

  isLoading: boolean;
  filterLoaded: boolean;
  deletingStatistics: boolean;
  errorMsg: ErrorType;
  isPro: boolean;
  viewSubscription: Subscription;
  menuHiddenChangeSubscription: Subscription;
  positionTop: number;

  pagedData: Pagination;
  paging: PagingFilter;
  filter: StatisticFilter;
  showDeleteSelected: boolean;

  columns: any[];
  columnsMobile: ColumnType[];

  ngOnInit(): void
  {
    this.isPro = this.playerProfileService.playerAuthenticationService.isProVersion();

    this.paging = {
      pageOffset: 0,
      pageSize: this.playerSessionService.statisticListPageSize,
      sortField: 'name',
      sortOrder: 1
    };
    console.log('Paging', this.paging);
    // Daten werden über StatisticsFilter geladen

    this.menuHiddenChangeSubscription = this.playerSessionService.onMenuHiddenChanged.subscribe(
      () => this.onSizeChanged());
  }

  ngAfterViewInit(): void
  {
    this.initColumns();

    this.viewSubscription = this.view.onSizeChanged.subscribe(() => this.onSizeChanged());
    setTimeout(() =>
    {
      this.onSizeChanged();
      this.filterLoaded = true;
    }, 100);
    setTimeout(() => this.onSizeChanged(), 250);
    setTimeout(() => this.onSizeChanged(), 500);
    setTimeout(() => this.onSizeChanged(), 1000);
  }

  ngOnDestroy(): void
  {
    this.viewSubscription?.unsubscribe();
    this.menuHiddenChangeSubscription?.unsubscribe();
  }

  private onSizeChanged(): void
  {
    const positionTopBefore = this.positionTop;
    const top = LayoutHelper.getTopFromElement('divMatches', this.view.isMobile ? 196 : 220);
    const footerHeight = document.getElementById('frontend_footer')?.offsetHeight || 40;
    this.positionTop = top + (this.view.isMobile ? 16 : 12) + footerHeight;
    if (positionTopBefore !== this.positionTop)
    {
      // console.log('positionTop: ' + this.positionTop + ', top: ' + top + ', footerHeight: ' + footerHeight);
      setTimeout(() => this.genericList?.onResize());
    }
  }

  private initColumns(): void
  {
    this.columns = [
      {
        key: 'DATE', header: 'DATE', field: 'date', datatype: 'template', width: '60', class: 'text-center',
        template: this.dateTemplate
      },
      {
        key: 'EI', header: 'EVENT', field: '', datatype: 'template', width: '120',
        template: this.eventInfoTemplate
      },
      {header: 'PARTICIPANTS', datatype: 'template', width: '',
        template: this.participantsTemplate},
      {header: 'RESULT', field: 'result', datatype: '', width: '', class: 'text-center'},
      {
        key: 'MI', header: 'INFO_SHORT', datatype: 'template', width: '70', template: this.matchInfoTemplate
      }
    ];

    this.columnsMobile = [
      {
        key: 'MAT',
        header: 'MATCHES'
      }
    ];
  }

  public filterChanged(filter: StatisticFilter): void
  {
    this.filter = filter;
    console.log('filterChanged: ', filter);
    this.loadData();
  }

  public loadData(): void
  {
    if (!this.isPro)
      return;
    this.isLoading = true;
    this.errorMsg = undefined;

    this.filter.pageLastDatetime = this.getLastObjectValueForPaging();
    this.playerProfileService.getStatisticsMatches(this.paging, this.filter).subscribe({
      next: data =>
      {
        console.log('StatisticMatches erhalten', data);
        this.pagedData = data;
        this.isLoading = false;
      },
      error: error =>
      {
        console.log('error', error);
        this.isLoading = false;
        this.errorMsg = this.restErrorHelper.getErrorType(error);
      }
    });
  }

  private getLastObjectValueForPaging(): Date
  {
    if (this.pagedData?.content?.length > 0)
      return this.pagedData?.content[this.pagedData.content.length - 1].date;
    else return undefined;
  }

  public lazyLoading(pageChangeInfo: PageChangeInfo): void
  {
    console.log('lazyLoading, pageOffset: ' + pageChangeInfo.pageOffset + ', pageSize: ' + pageChangeInfo.pageSize);
    this.playerSessionService.statisticListPageSize = pageChangeInfo.pageSize;

    if (pageChangeInfo.pageOffset >= 0)
      this.paging.pageOffset = pageChangeInfo.pageOffset;
    this.paging.pageSize = pageChangeInfo.pageSize;

    this.loadData();
  }

  public onRowSelect(item: any): void
  {
    console.log('Item', item);
    if (item?.data)
    {
      const match: StatisticMatch = item.data;
      EventMatchDetailsComponent.showDialogPlayerProfile(this.dialogService, this.frontendService, this.restErrorHelper,
        match, undefined);
    }
  }

  public showDeleteChanged(value: boolean): void
  {
    this.showDeleteSelected = value;
    console.log('showDeleteSelected: ' + this.showDeleteSelected);
    this.loadData();
  }

  public rowDelete(event: any): void
  {
    console.log('rowDelete: ', event);

    this.confirmationService.confirm({
      message: this.translate.instant('PLAYER_PROFILE_DELETE_STATISTIC_QST'),
      header: this.translate.instant('ACTION_DELETE'),
      icon: 'pi pi-question-circle',
      acceptLabel: this.translate.instant('YES'),
      rejectLabel: this.translate.instant('NO'),
      rejectButtonStyleClass: 'button-no button-right',
      accept: () =>
      {
        this.deletingStatistics = true;
        this.playerProfileService.deleteStatisticsMatch(event.database, event.matchId).subscribe({
          next: data =>
          {
            console.log('StatisticMatch erfolgreich gelöscht', data);
            this.deletingStatistics = false;
            this.loadData();
          },
          error: error =>
          {
            this.deletingStatistics = false;
            this.restErrorHelper.handleErrorService(error);
          }
        });
      }
    });
  }
}
